import { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import CodeMirror from '@uiw/react-codemirror';
import { yaml as yamlLang } from '@codemirror/lang-yaml';
import { api, ActionRun, ClientDetail as ClientDetailT, ClientInventory, CurrentAgent } from '../lib/api';
import { relativeTime, absoluteTime, shortHash, bytes } from '../lib/format';
import { ErrorState } from '../components/QueryState';
import { parseYaml } from '../lib/manifestModel';

export function ClientDetail() {
  const { uuid } = useParams<{ uuid: string }>();
  const qc = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['client', uuid],
    queryFn: () => api.get<ClientDetailT>(`/clients/${uuid}`),
    enabled: !!uuid,
  });

  if (isError) return <ErrorState error={error} onRetry={() => refetch()} />;
  if (isLoading || !data) return <div className="text-muted">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-baseline gap-3">
        <Link to="/clients" className="text-sm text-accent hover:underline">← Clients</Link>
        <h1 className="text-xl font-semibold font-mono">{data.uuid}</h1>
        {data.blocked && <span className="badge badge-blocked">blocked</span>}
        {data.last_status === 'error' && <span className="badge badge-error">error</span>}
        {data.last_status === 'ok' && <span className="badge badge-ok">ok</span>}
      </div>

      <section className="card grid grid-cols-2 gap-4">
        <Info label="Hostname">{data.hostname || '—'}</Info>
        <Info label="Machine fingerprint">
          {data.fingerprint
            ? <span className="font-mono text-xs">{data.fingerprint}</span>
            : <span className="text-muted">— (pre-fingerprint enrollment)</span>}
        </Info>
        <Info label="First seen">{absoluteTime(data.first_seen ?? undefined)}</Info>
        <Info label="Last seen" hint={absoluteTime(data.last_seen ?? undefined)}>{relativeTime(data.last_seen ?? undefined)}</Info>
        <Info label="Last error">{data.last_error || <span className="text-muted">none</span>}</Info>
        <Info
          label="Agent version"
          hint={
            data.agent_sha256
              ? `Reported by host: ${data.agent_sha256}${data.current_agent?.script?.sha256 ? `\nServer current: ${data.current_agent.script.sha256}` : ''}`
              : 'No agent_sha256 reported. Host is running a pre-self-update agent — re-run install.sh to upgrade.'
          }
        >
          <AgentVersionBadge reported={data.agent_sha256} expected={data.current_agent?.script?.sha256 ?? null} />
        </Info>
      </section>

      <InventoryPanel inventory={data.inventory} inventoryAt={data.inventory_at} currentAgent={data.current_agent} />

      <Drift drift={data.drift} />

      <NotesAndTags client={data} onSaved={() => qc.invalidateQueries({ queryKey: ['client', uuid] })} />

      <RunHistory uuid={data.uuid} initial={data.recent_runs} initialTotal={data.runs_total} />

      <OverlayEditor uuid={data.uuid} />

      <DangerZone uuid={data.uuid} />
    </div>
  );
}

function Info({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div>
      <div className="text-xs text-muted">{label}</div>
      <div title={hint}>{children}</div>
    </div>
  );
}

function AgentVersionBadge({ reported, expected }: { reported: string | null; expected: string | null }) {
  if (!reported) {
    return (
      <span className="space-x-1">
        <span className="text-muted">not reported</span>
        <span className="badge badge-error">needs reinstall</span>
      </span>
    );
  }
  if (!expected) {
    return <span className="font-mono text-xs">{shortHash(reported)}</span>;
  }
  return (
    <span className="space-x-1">
      <span className="font-mono text-xs">{shortHash(reported)}</span>
      {reported === expected ? (
        <span className="badge badge-ok">current</span>
      ) : (
        <span className="badge badge-error">behind</span>
      )}
    </span>
  );
}

function InventoryPanel({ inventory, inventoryAt, currentAgent }: { inventory: ClientInventory | null; inventoryAt: number | null; currentAgent: CurrentAgent }) {
  if (!inventory || !inventoryAt) {
    return (
      <section className="card">
        <h2 className="font-semibold mb-2">Inventory</h2>
        <div className="text-muted text-sm">
          No inventory snapshot yet. Add the <code>inventory</code> action from the
          {' '}<Link to="/manifest" className="text-accent hover:underline">manifest editor</Link> to start collecting.
        </div>
      </section>
    );
  }
  const mem = inventory.memory;
  const cpu = inventory.cpu;
  const os = inventory.os;
  const reportedAgentSha = inventory.agent?.host_customize_sha256;
  const currentAgentSha = currentAgent?.script?.sha256 ?? null;
  const agentMatches = reportedAgentSha && currentAgentSha ? reportedAgentSha === currentAgentSha : null;
  return (
    <section className="card space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 className="font-semibold">Inventory</h2>
        <div className="text-xs text-muted" title={absoluteTime(inventoryAt ?? undefined)}>
          collected {relativeTime(inventoryAt ?? undefined)}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <Info label="OS">{os?.name ? `${os.name} ${os.version || ''}`.trim() : '—'}</Info>
        <Info label="Kernel"><span className="font-mono text-xs">{os?.kernel || '—'}</span></Info>
        <Info label="Arch">{os?.arch || '—'}</Info>
        <Info label="Uptime">{inventory.uptime_seconds ? formatUptime(inventory.uptime_seconds) : '—'}</Info>
        <Info label="CPU">{cpu?.model || '—'}</Info>
        <Info label="Cores">{cpu?.cores ?? '—'}</Info>
        <Info label="Load (1/5/15)">{cpu ? `${fmtLoad(cpu.load1)} / ${fmtLoad(cpu.load5)} / ${fmtLoad(cpu.load15)}` : '—'}</Info>
        <Info label="RAM">
          {mem?.total ? (
            <span>
              {bytes(mem.total)} total · {mem.used_pct?.toFixed(1)}% used
              {mem.available !== undefined ? ` · ${bytes(mem.available)} available` : ''}
            </span>
          ) : '—'}
        </Info>
        <Info
          label="Agent sha256"
          hint={
            reportedAgentSha
              ? `Reported by host: ${reportedAgentSha}${currentAgentSha ? `\nServer current: ${currentAgentSha}` : ''}`
              : undefined
          }
        >
          {reportedAgentSha ? (
            <span className="space-x-1">
              <span className="font-mono text-xs">{shortHash(reportedAgentSha)}</span>
              {agentMatches === true && <span className="badge badge-ok">current</span>}
              {agentMatches === false && <span className="badge badge-error">behind</span>}
            </span>
          ) : (
            '—'
          )}
        </Info>
      </div>

      {inventory.filesystems && inventory.filesystems.length > 0 && (
        <div>
          <div className="text-xs text-muted mb-1">Filesystems</div>
          <table className="table">
            <thead>
              <tr><th>Mount</th><th>Type</th><th>Total</th><th>Used</th><th>Free</th><th>Used %</th></tr>
            </thead>
            <tbody>
              {inventory.filesystems.map((f) => (
                <tr key={f.mount}>
                  <td className="font-mono text-xs">{f.mount}</td>
                  <td>{f.fstype || '—'}</td>
                  <td>{bytes(f.total)}</td>
                  <td>{bytes(f.used)}</td>
                  <td>{bytes(f.free)}</td>
                  <td className={f.used_pct >= 90 ? 'text-danger' : f.used_pct >= 75 ? 'text-fg' : 'text-muted'}>
                    {f.used_pct.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {inventory.network && inventory.network.length > 0 && (
        <div>
          <div className="text-xs text-muted mb-1">Network</div>
          <table className="table">
            <thead><tr><th>Interface</th><th>IPv4</th><th>MAC</th></tr></thead>
            <tbody>
              {inventory.network.map((n) => (
                <tr key={n.iface}>
                  <td className="font-mono text-xs">{n.iface}</td>
                  <td className="font-mono text-xs">{n.ipv4 || '—'}</td>
                  <td className="font-mono text-xs">{n.mac || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function fmtLoad(n: number | undefined) {
  return n !== undefined ? n.toFixed(2) : '—';
}

function formatUptime(s: number) {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
}

function Drift({ drift }: { drift: ClientDetailT['drift'] }) {
  return (
    <section className="card">
      <h2 className="font-semibold mb-3">Drift</h2>
      {drift.length === 0 ? (
        <div className="text-success text-sm">All files in sync with the current manifest.</div>
      ) : (
        <table className="table">
          <thead>
            <tr><th>Dest</th><th>Expected</th><th>Applied</th><th>State</th></tr>
          </thead>
          <tbody>
            {drift.map((d) => (
              <tr key={d.dest}>
                <td className="font-mono text-xs">{d.dest}</td>
                <td className="font-mono text-xs">{shortHash(d.expected)}</td>
                <td className="font-mono text-xs">{shortHash(d.applied)}</td>
                <td>
                  {d.expected === null
                    ? <span className="badge">extraneous</span>
                    : d.applied === null
                    ? <span className="badge badge-error">missing</span>
                    : <span className="badge badge-error">out of date</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function NotesAndTags({ client, onSaved }: { client: ClientDetailT; onSaved: () => void }) {
  const [notes, setNotes] = useState(client.notes);
  const [tagsRaw, setTagsRaw] = useState(client.tags.join(', '));
  useEffect(() => {
    setNotes(client.notes);
    setTagsRaw(client.tags.join(', '));
  }, [client.uuid, client.notes, client.tags]);
  const m = useMutation({
    mutationFn: () =>
      api.patch(`/clients/${client.uuid}`, {
        notes,
        tags: tagsRaw.split(',').map((t) => t.trim()).filter(Boolean),
      }),
    onSuccess: onSaved,
  });
  return (
    <section className="card space-y-3">
      <h2 className="font-semibold">Notes & tags</h2>
      <label className="text-sm flex flex-col gap-1">
        <span className="text-muted">Tags (comma separated)</span>
        <input className="input" value={tagsRaw} onChange={(e) => setTagsRaw(e.target.value)} />
      </label>
      <label className="text-sm flex flex-col gap-1">
        <span className="text-muted">Notes</span>
        <textarea className="textarea" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>
      <div>
        <button className="btn btn-primary" onClick={() => m.mutate()} disabled={m.isPending}>
          {m.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
      {m.isError && <div className="text-danger text-sm">{m.error.message}</div>}
    </section>
  );
}

type RunsResponse = { runs: ActionRun[]; total: number };

function RunHistory({ uuid, initial, initialTotal }: { uuid: string; initial: ActionRun[]; initialTotal: number }) {
  const [nameFilter, setNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'ok' | 'error'>('');
  const [sinceFilter, setSinceFilter] = useState<'' | '1h' | '24h' | '7d'>('');
  const [limit, setLimit] = useState(50);

  const sinceParam = useMemo(() => {
    if (!sinceFilter) return undefined;
    const now = Math.floor(Date.now() / 1000);
    const map: Record<string, number> = { '1h': 3600, '24h': 86400, '7d': 7 * 86400 };
    return String(now - map[sinceFilter]);
  }, [sinceFilter]);

  const filtersActive = !!nameFilter || !!statusFilter || !!sinceFilter || limit > 50;
  const params = new URLSearchParams();
  if (nameFilter) params.set('name', nameFilter);
  if (statusFilter) params.set('status', statusFilter);
  if (sinceParam) params.set('since', sinceParam);
  params.set('limit', String(limit));

  const { data: paged, isFetching } = useQuery({
    queryKey: ['runs', uuid, nameFilter, statusFilter, sinceFilter, limit],
    queryFn: () => api.get<RunsResponse>(`/clients/${uuid}/runs?${params.toString()}`),
    enabled: filtersActive,
    placeholderData: (prev) => prev,
  });

  const rows = filtersActive ? (paged?.runs || []) : initial;
  const total = filtersActive ? (paged?.total ?? 0) : initialTotal;
  const allKnownNames = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows) s.add(r.name);
    for (const r of initial) s.add(r.name);
    return [...s].sort();
  }, [rows, initial]);

  return (
    <section className="card space-y-3">
      <div className="flex items-baseline justify-between">
        <h2 className="font-semibold">Run history</h2>
        <div className="text-xs text-muted">
          showing {rows.length} of {total}{total >= 200 ? ' (oldest pruned beyond 200)' : ''}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-end">
        <label className="text-sm">
          <div className="text-muted">Action</div>
          <select className="select" value={nameFilter} onChange={(e) => setNameFilter(e.target.value)}>
            <option value="">any</option>
            {allKnownNames.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <div className="text-muted">Status</div>
          <select className="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as '' | 'ok' | 'error')}>
            <option value="">any</option>
            <option value="ok">ok</option>
            <option value="error">error</option>
          </select>
        </label>
        <label className="text-sm">
          <div className="text-muted">Since</div>
          <select className="select" value={sinceFilter} onChange={(e) => setSinceFilter(e.target.value as '' | '1h' | '24h' | '7d')}>
            <option value="">all</option>
            <option value="1h">1 hour</option>
            <option value="24h">24 hours</option>
            <option value="7d">7 days</option>
          </select>
        </label>
        {filtersActive && (
          <button
            className="btn"
            onClick={() => { setNameFilter(''); setStatusFilter(''); setSinceFilter(''); setLimit(50); }}
          >
            Reset
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="text-muted text-sm">No runs match the current filters.</div>
      ) : (
        <table className="table">
          <thead>
            <tr><th>When</th><th>Name</th><th>Status</th><th>Exit</th><th>Output</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td title={absoluteTime(r.ts)} className="whitespace-nowrap">{relativeTime(r.ts)}</td>
                <td className="font-mono">{r.name}</td>
                <td>
                  {r.status === 'ok'
                    ? <span className="badge badge-ok">ok</span>
                    : <span className="badge badge-error">{r.status}</span>}
                </td>
                <td>{r.exit ?? <span className="text-muted">—</span>}</td>
                <td className="max-w-2xl">
                  {r.output ? (
                    <details>
                      <summary className="text-xs text-muted cursor-pointer">
                        {r.output.length} chars · expand
                      </summary>
                      <pre className="font-mono text-xs whitespace-pre-wrap mt-1">{r.output}</pre>
                    </details>
                  ) : <span className="text-muted">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="flex gap-2 items-center">
        <button
          className="btn"
          disabled={rows.length >= total || isFetching}
          onClick={() => setLimit((l) => Math.min(l + 50, 200))}
        >
          {isFetching ? 'Loading…' : 'Load more'}
        </button>
        <span className="text-xs text-muted">history capped at 200 runs per client</span>
      </div>
    </section>
  );
}

function OverlayEditor({ uuid }: { uuid: string }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['overlay', uuid],
    queryFn: () => api.get<{ yaml: string; updated_at: number | null }>(`/manifest/clients/${uuid}`),
  });
  const [text, setText] = useState('');
  const [errors, setErrors] = useState<string | null>(null);
  useEffect(() => { if (data) setText(data.yaml); }, [data?.yaml]);
  const save = useMutation({
    mutationFn: () => {
      if (text.trim() !== '') {
        const parsed = parseYaml(text);
        if (!parsed.ok) throw new Error(parsed.error);
      }
      return api.put(`/manifest/clients/${uuid}`, { yaml: text });
    },
    onSuccess: () => {
      setErrors(null);
      qc.invalidateQueries({ queryKey: ['overlay', uuid] });
      qc.invalidateQueries({ queryKey: ['client', uuid] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (e: Error & { details?: { errors?: Array<{ path: string; message: string }> } }) => {
      setErrors(e.details?.errors ? e.details.errors.map((x) => `${x.path}: ${x.message}`).join('\n') : e.message);
    },
  });
  const del = useMutation({
    mutationFn: () => api.delete(`/manifest/clients/${uuid}`),
    onSuccess: () => {
      setText('');
      qc.invalidateQueries({ queryKey: ['overlay', uuid] });
      qc.invalidateQueries({ queryKey: ['client', uuid] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (e: Error) => setErrors(e.message),
  });
  return (
    <section className="card space-y-3">
      <h2 className="font-semibold">Per-client overlay (YAML)</h2>
      <p className="text-sm text-muted">
        Overlay entries match by <code>name</code> and override the global manifest. Set
        <code> dest: null</code> on a file (or <code>command: null</code> on an action) to remove it for this client.
        Save empty to remove the overlay entirely.
      </p>
      <CodeMirror value={text} height="280px" extensions={[yamlLang()]} onChange={setText} />
      {errors && <pre className="text-danger text-xs whitespace-pre-wrap">{errors}</pre>}
      <div className="flex gap-2">
        <button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>Save</button>
        <button
          className="btn btn-danger"
          disabled={del.isPending}
          onClick={() => { if (confirm('Remove this client\'s overlay?')) del.mutate(); }}
        >
          Remove overlay
        </button>
      </div>
    </section>
  );
}

function DangerZone({ uuid }: { uuid: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const del = useMutation({
    mutationFn: () => api.delete(`/clients/${uuid}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      navigate('/clients', { replace: true });
    },
  });
  return (
    <section className="card border-danger/40">
      <h2 className="font-semibold text-danger mb-2">Danger zone</h2>
      <p className="text-sm text-muted mb-2">
        Deleting removes this client's record. The host will re-register on its next check-in.
      </p>
      <button
        className="btn btn-danger"
        onClick={() => { if (confirm('Delete client record?')) del.mutate(); }}
      >
        Delete client
      </button>
      {del.isError && <div className="text-danger text-sm mt-2">{del.error.message}</div>}
    </section>
  );
}
